import posthog from "posthog-js";

interface AnalyticsConfig {
  apiKey: string;
  apiHost?: string;
  isDevelopment?: boolean;
  platform?: "desktop" | "web";
  appVersion?: string;
  defaults?: string;
  personProfiles?: "identified_only" | "always";
}

class AnalyticsService {
  private initialized = false;
  private platform: "desktop" | "web" = "web";
  private isMinimized = false;

  init(config: AnalyticsConfig) {
    console.log("[Analytics] Initializing with config:", {
      platform: config.platform,
      isDevelopment: config.isDevelopment,
      hasApiKey: !!config.apiKey,
    });

    if (this.initialized || !config.apiKey) {
      console.log("[Analytics] Already initialized or no API key");
      return;
    }

    this.platform = config.platform || "web";

    // Analytics is now enabled in all environments
    // You can disable it by setting DISABLE_ANALYTICS=true in your environment
    const disableAnalytics =
      typeof process !== "undefined"
        ? process.env?.DISABLE_ANALYTICS === "true"
        : typeof window !== "undefined" && (window as any).DISABLE_ANALYTICS;

    if (disableAnalytics) {
      console.log("[Analytics] Disabled by environment variable");
      return;
    }

    posthog.init(config.apiKey, {
      api_host: config.apiHost || "https://app.posthog.com",
      defaults: config.defaults,
      person_profiles: config.personProfiles || "identified_only",

      // Manual pageview tracking for SPAs
      capture_pageview: false,

      // Enable session recording
      enable_recording_console_log: true,
      session_recording: {maskTextSelector: ".sensitive", maskAllInputs: false},

      // Persist user across sessions
      persistence: "localStorage",

      // Set up platform context
      loaded: (posthog) => {
        posthog.register({
          platform: this.platform,
          app_type: this.platform === "desktop" ? "desktop" : "web",
          app_name: this.platform === "desktop" ? "Zeg Desktop" : "Zeg Web",
          app_version: config.appVersion || "unknown",
          environment: config.isDevelopment ? "development" : "production",
        });
      },
    });

    this.initialized = true;
    this.setupWindowListeners();
  }

  private setupWindowListeners() {
    if (this.platform !== "desktop") return;

    // Track window state changes for desktop app
    if (typeof window !== "undefined") {
      window.addEventListener("blur", () => {
        this.isMinimized = true;
        this.track("app_minimized");
        // Pause session recording when minimized
        posthog.stopSessionRecording();
      });

      window.addEventListener("focus", () => {
        if (this.isMinimized) {
          this.isMinimized = false;
          this.track("app_restored");
          // Resume session recording when restored
          posthog.startSessionRecording();
        }
      });
    }
  }

  identify(userId: string, traits?: Record<string, any>) {
    if (!this.initialized) return;

    posthog.identify(userId, {...traits, platform: this.platform});
  }

  track(event: string, properties?: Record<string, any>) {
    if (!this.initialized) return;

    // Don't track events when minimized (except the minimize event itself)
    if (this.isMinimized && event !== "app_minimized") return;

    posthog.capture(event, {
      ...properties,
      app_name: this.platform === "desktop" ? "Zeg Desktop" : "Zeg Web",
      platform: this.platform,
      timestamp: new Date().toISOString(),
    });
  }

  trackPageView(pageName: string, properties?: Record<string, any>) {
    this.track("$pageview", {page: pageName, ...properties});
  }

  setUserProperty(key: string, value: any) {
    if (!this.initialized) return;

    posthog.people.set({[key]: value});
  }

  // Set session-level properties that persist across events but not across sessions
  setSessionProperties(properties: Record<string, any>) {
    if (!this.initialized) return;

    posthog.register(properties);
  }

  // Update workspace/profile context
  updateContext(context: {
    workspaceId?: string;
    profileId?: string;
    profileName?: string;
  }) {
    if (!this.initialized) return;

    // These properties will be included in all subsequent events
    this.setSessionProperties({
      current_workspace_id: context.workspaceId,
      current_profile_id: context.profileId,
      current_profile_name: context.profileName,
      context_updated_at: new Date().toISOString(),
    });

    // Also track the context switch as an event
    this.track("workspace_context_changed", context);
  }

  startSessionRecording() {
    if (!this.initialized) return;
    posthog.startSessionRecording();
  }

  stopSessionRecording() {
    if (!this.initialized) return;
    posthog.stopSessionRecording();
  }

  reset() {
    if (!this.initialized) return;
    posthog.reset();
  }

  // Helper method to track common product events
  trackFeatureUsage(featureName: string, metadata?: Record<string, any>) {
    this.track("feature_used", {feature_name: featureName, ...metadata});
  }

  // Track errors for debugging
  trackError(error: Error, context?: Record<string, any>) {
    this.track("error_occurred", {
      error_message: error.message,
      error_stack: error.stack,
      ...context,
    });
  }

  // Capture exceptions using PostHog's exception tracking
  captureException(
    error: Error | string,
    additionalProperties?: Record<string, any>
  ) {
    if (!this.initialized) return;

    // PostHog's captureException method
    posthog.captureException(error, additionalProperties);
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

// Export class for testing or multiple instances
export {AnalyticsService};
